import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socket, { postRequest, showMessage } from '../../config';
import { toggleModal } from '../../reducers/modalReducer';
import { updateFileAllocation } from '../../reducers/filesAllocationReducer';
import { SHOW_AI_FEATURES } from '../../env';
import './autoFill.css';

/**
 * AutoFillButton - Triggers auto-fill for a single question, section, or module
 *
 * Props:
 * - companyId: Company ID
 * - moduleType: Module type (e.g., 'repository')
 * - sectionCode: Optional section code (e.g., 'D001')
 * - questionId: Optional question ID for single question fill
 * - qNo: Optional question number
 * - scope: 'question' | 'section' | 'module'
 * - scopeName: Display name for scope (e.g., 'Investor Materials', 'Investment Readiness')
 * - onStart: Callback when auto-fill starts (for progress modal)
 * - onComplete: Callback when auto-fill completes
 * - disabled: Disable the button
 * - size: 'small' | 'medium' - Button size
 */
const AutoFillButton = ({
    companyId,
    moduleType,
    moduleId = null,
    sectionCode,
    questionId,
    qNo,
    scope = 'question',
    scopeName = '',
    onStart,
    onComplete,
    disabled = false,
    size = 'small'
}) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const isAutoFilling = useRef(false);
    const { selectedCompany: company } = useSelector(state => state.company);
    const isMinimized = useSelector(state => !!state.modal.autoFillStatusBarVisible);
    const isMinimizedRef = useRef(false);
    const activePhase = useSelector(state => state.modal.autoFillStatusBarPhase);
    const isAutoFillActive = ['uploading', 'indexing', 'allocated', 'filling'].includes(activePhase);

    const progressEvent = 'autoFill:progress';

    // Keep ref in sync so async functions always see latest value
    useEffect(() => {
        isMinimizedRef.current = isMinimized;
    }, [isMinimized]);

    // Listen for socket progress updates (for modal only - not when minimized to status bar)
    useEffect(() => {
        const handleProgress = (data) => {
            if (!isAutoFilling.current) return;
            dispatch(toggleModal({
                autoFillTotal: data.total || 0,
                autoFillCurrent: data.current || 0,
                autoFillCurrentQuestion: data.currentQuestion || '',
                autoFillCompleted: false
            }));
        };

        socket.on(progressEvent, handleProgress);

        return () => {
            socket.off(progressEvent, handleProgress);
        };
    }, [dispatch, progressEvent]);

    const handleClick = () => {
        if (loading || disabled) return;
        if (isAutoFillActive) {
            showMessage('Auto-fill is already running. Please wait for it to complete.');
            return;
        }

        // For single question, auto-fill immediately
        // For section/module, open stepper modal at 'select' step
        if (scope === 'question') {
            startAutoFill();
        } else {
            dispatch(toggleModal({
                open: 'autoFillStepper',
                autoFillStepperPhase: 'select',
                autoFillModuleName: scope === 'section'
                    ? `${sectionCode || ''}${sectionCode && scopeName ? ' ' : ''}${scopeName || ''}`.trim() || sectionCode || ''
                    : company?.modules?.[moduleType]?.heading || scopeName || moduleType || '',
                autoFillStatusBarPhase: 'select',
                autoFillWithFilling: true,
                autoFillUploadMode: 'files',
            }));
            dispatch(updateFileAllocation({
                autoFillContext: { enabled: true, companyId, moduleType, moduleId: moduleId || null, sectionCode, scope, scopeName },
                tab: moduleType
            }));
        }
    };

    // File upload and skip logic moved to AutoFillStepperModal (select step)

    const startAutoFill = async () => {
        setLoading(true);

        const payload = {
            companyId,
            moduleType,
            ...(moduleId ? { moduleId } : {}),
            minConfidence: 0.7
        };

        if (scope === 'question' && (questionId || qNo)) {
            payload.questionId = questionId;
            payload.qNo = qNo;
            payload.maxQuestions = 1;
        } else if (scope === 'section' && sectionCode) {
            payload.sectionCode = sectionCode;
            payload.maxQuestions = 50;
        } else {
            payload.maxQuestions = 100;
        }

        // Show progress modal for section/module scope using stepper modal
        if (scope !== 'question') {
            // Only enable socket listener for section/module scope
            isAutoFilling.current = true;

            dispatch(toggleModal({
                open: 'autoFillStepper',
                autoFillStepperPhase: 'filling',
                autoFillModuleName: company?.modules?.[moduleType]?.heading || moduleType || '',
                autoFillTotal: 0,
                autoFillCurrent: 0,
                autoFillCurrentQuestion: '',
                autoFillCompleted: false,
                autoFillProcessed: 0,
                autoFillAnswered: 0,
                autoFillNeedsReview: 0,
                autoFillSkipped: 0,
                autoFillStatusBarPhase: 'filling',
            }));
            // Also notify parent if callback provided
            onStart?.({ scope, moduleType, sectionCode, total: payload.maxQuestions });

            // Small delay to ensure modal is rendered before API call
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        try {
            const response = await postRequest('/clarence/auto-fill', payload);

            if (response?.code === 200) {
                const { answered, needsReview, skipped, results, processed } = response?.body || {};

                // Handle case where question is already answered
                if (processed === 0) {
                    if (scope !== 'question') {
                        dispatch(toggleModal({ open: '' }));
                    }
                    showMessage('Question is already answered');
                    onComplete?.(response?.body);
                    return;
                }

                if (scope === 'question') {
                    // For single question, show toast message
                    const result = results?.[0];
                    let message = '';
                    if (result?.status === 'answered') {
                        message = `Answered: ${result.answer} (${Math.round(result.confidence * 100)}% confidence)`;
                    } else if (result?.status === 'needs_review') {
                        message = `Needs review: ${result.answer} (${Math.round(result.confidence * 100)}% confidence)`;
                    } else {
                        message = result?.reason === 'no_relevant_docs' ? 'No relevant documents found' : 'Could not auto-fill';
                    }
                    showMessage(message);
                } else {
                    // For section/module, show results in stepper modal (result step)
                    // Don't force-open if user minimized the modal
                    dispatch(toggleModal({
                        ...(isMinimizedRef.current ? {} : { open: 'autoFillStepper' }),
                        autoFillStepperPhase: 'result',
                        autoFillCompleted: true,
                        autoFillProcessed: processed || 0,
                        autoFillAnswered: answered || 0,
                        autoFillNeedsReview: needsReview || 0,
                        autoFillSkipped: skipped || 0,
                        autoFillStatusBarPhase: 'completed',
                    }));
                }

                onComplete?.(response?.body);
            } else {
                if (scope !== 'question') {
                    dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error', autoFillError: response?.message || 'Auto-fill failed' }));
                }
                showMessage(response?.message || 'Auto-fill failed');
            }
        } catch (error) {
            console.error('Auto-fill error:', error);
            // Close modal on error + set status bar error
            if (scope !== 'question') dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error', autoFillError: 'Auto-fill failed. Please try again.' }));
            showMessage('Auto-fill failed. Please try again.');
        } finally {
            setLoading(false);
            isAutoFilling.current = false;
        }
    };

    const buttonClass = size === 'small' ? 'auto-fill-btn auto-fill-btn-small' : 'auto-fill-btn auto-fill-btn-medium';

    const icon = loading ? (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ animation: 'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" />
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 56" version="1.1" width="50" height="50">
            <path d="M 31.233 11.238 C 30.508 12.124, 30.592 12.860, 31.513 13.708 C 32.541 14.654, 33.063 14.629, 33.925 13.590 C 34.738 12.611, 34.665 11.967, 33.645 11.120 C 32.601 10.254, 32.014 10.283, 31.233 11.238 M 32.063 23.474 C 19.120 36.545, 18.766 37.015, 20.225 39.224 C 23.176 43.690, 24.949 42.712, 38.791 28.982 C 46.056 21.775, 52 15.301, 52 14.595 C 52 12.915, 48.748 10, 46.874 10 C 46.066 10, 39.401 16.063, 32.063 23.474 M 43.196 15.298 C 37.400 21.278, 40.114 24.247, 46.065 18.437 C 49.043 15.529, 49.693 14.335, 48.852 13.322 C 47.284 11.433, 46.767 11.614, 43.196 15.298 M 23 15.383 C 23 16.144, 22.055 17.014, 20.899 17.316 C 18.648 17.905, 19.163 20, 21.559 20 C 22.352 20, 23 20.648, 23 21.441 C 23 23.837, 25.095 24.352, 25.684 22.101 C 25.986 20.945, 26.856 20, 27.617 20 C 28.377 20, 29 19.325, 29 18.500 C 29 17.675, 28.325 17, 27.500 17 C 26.675 17, 26 16.325, 26 15.500 C 26 14.675, 25.325 14, 24.500 14 C 23.675 14, 23 14.623, 23 15.383 M 29.005 29.495 C 22.390 36.155, 21.728 37.156, 23.060 38.488 C 24.392 39.821, 25.432 39.109, 32.403 32.098 C 39.568 24.890, 41.075 22, 37.666 22 C 36.997 22, 33.100 25.373, 29.005 29.495 M 46 32 C 46 33.333, 45.333 34, 44 34 C 42.900 34, 42 34.450, 42 35 C 42 35.550, 42.900 36, 44 36 C 45.333 36, 46 36.667, 46 38 C 46 39.100, 46.450 40, 47 40 C 47.550 40, 48 39.283, 48 38.406 C 48 37.530, 48.900 36.577, 50 36.290 C 52.381 35.667, 52.677 34, 50.406 34 C 49.530 34, 48.577 33.100, 48.290 32 C 48.002 30.900, 47.369 30, 46.883 30 C 46.398 30, 46 30.900, 46 32" stroke="none" fill="#6d5cf0" fill-rule="evenodd" />
        </svg>
    );
    const badgeLabel = loading ? 'Filling...' : scope === 'section' ? 'Auto Fill Section' : scope === 'module' ? 'Auto Fill Module' : 'Auto Fill';

    const getScopeName = () => {
        if (scopeName) return scopeName;
        if (scope === 'section') return sectionCode || 'this section';
        if (scope === 'module') return moduleType || 'this module';
        return 'this question';
    };

    if (!SHOW_AI_FEATURES) return null;

    return (
        <>
            <div className={`auto-fill-icon-wrap ${(disabled || isAutoFillActive) ? 'auto-fill-btn-disabled' : ''}`}>
                <button
                    onClick={handleClick}
                    disabled={loading || disabled || isAutoFillActive}
                    className={`auto-fill-btn ${loading ? 'auto-fill-btn-loading' : ''}`}
                    style={{ padding: '0.2rem', minWidth: 'unset', background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    {icon}
                </button>
                {badgeLabel && <span className="auto-fill-hover-chip">{badgeLabel}</span>}
            </div>

            {/* File input + confirmation modal moved to AutoFillStepperModal (select step) */}
        </>
    );
};

export default AutoFillButton;
